import { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Minus, Plus, Search, ShoppingCart, Copy, Send, Trash2, LogOut } from "lucide-react";
import { listinoMenci } from "@/data/listinoMenci";

const Ordine = () => {
  const navigate = useNavigate();
  
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [pezziPerUnita, setPezziPerUnita] = useState<Record<string, number>>({});
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  
  const [search, setSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .single();
      if (!roles) {
        toast.error("Accesso negato");
        navigate("/");
        return;
      }
      const { data: config, error } = await supabase
        .from("menci_item_config")
        .select("item_key, pezzi");
      if (error) {
        toast.error("Errore caricamento pezzi", { description: error.message });
      } else if (config) {
        const map: Record<string, number> = {};
        for (const row of config) map[row.item_key] = row.pezzi;
        setPezziPerUnita(map);
      }
      setAuthorized(true);
      setLoading(false);
    })();
  }, [navigate]);

  const keyOf = (cat: string, nome: string, formato: string) => `${cat}__${nome}__${formato}`;

  const setQty = (k: string, v: number) => {
    setQuantities((q) => {
      const n = Math.max(0, Math.floor(v || 0));
      const next = { ...q };
      if (n === 0) delete next[k];
      else next[k] = n;
      return next;
    });
  };

  const getPezzi = (k: string) => pezziPerUnita[k] ?? 1;

  const persistPezzi = async (k: string, n: number) => {
    setSavingKeys((s) => ({ ...s, [k]: true }));
    const { data: { session } } = await supabase.auth.getSession();
    const { error } = await supabase
      .from("menci_item_config")
      .upsert(
        { item_key: k, pezzi: n, updated_by: session?.user.id ?? null, updated_at: new Date().toISOString() },
        { onConflict: "item_key" }
      );
    setSavingKeys((s) => {
      const next = { ...s };
      delete next[k];
      return next;
    });
    if (error) {
      toast.error("Errore salvataggio", { description: error.message });
    }
  };

  const debounceTimers = useMemo(() => new Map<string, ReturnType<typeof setTimeout>>(), []);

  const setPezzi = (k: string, v: number) => {
    const n = Math.max(1, Math.floor(v || 1));
    setPezziPerUnita((p) => ({ ...p, [k]: n }));
    const existing = debounceTimers.get(k);
    if (existing) clearTimeout(existing);
    debounceTimers.set(
      k,
      setTimeout(() => {
        persistPezzi(k, n);
        debounceTimers.delete(k);
      }, 600)
    );
  };


  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return listinoMenci;
    return listinoMenci
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (i) =>
            i.nome.toLowerCase().includes(s) ||
            i.formato.toLowerCase().includes(s) ||
            c.categoria.toLowerCase().includes(s)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [search]);

  const selectedRows = useMemo(() => {
    const rows: { categoria: string; nome: string; formato: string; prezzo: number; qta: number; pezzi: number; pezziTot: number; totale: number }[] = [];
    for (const c of listinoMenci) {
      for (const i of c.items) {
        const k = keyOf(c.categoria, i.nome, i.formato);
        const qta = quantities[k];
        if (qta && qta > 0) {
          const pezzi = pezziPerUnita[k] ?? 1;
          rows.push({
            categoria: c.categoria,
            nome: i.nome,
            formato: i.formato,
            prezzo: i.prezzo,
            qta,
            pezzi,
            pezziTot: qta * pezzi,
            totale: i.prezzo * qta * pezzi,
          });
        }
      }
    }
    return rows;
  }, [quantities, pezziPerUnita]);

  const totaleConf = selectedRows.reduce((s, r) => s + r.qta, 0);
  const totalePezzi = selectedRows.reduce((s, r) => s + r.pezziTot, 0);
  const totaleEuro = selectedRows.reduce((s, r) => s + r.totale, 0);

  const messaggio = useMemo(() => {
    const data = new Date().toLocaleDateString("it-IT");
    let m = `🌱 *Ordine Mangimi Rubini*\n📅 Data: ${data}\n`;
    m += `\n--- ARTICOLI ---\n`;
    const byCat = selectedRows.reduce<Record<string, typeof selectedRows>>((acc, r) => {
      (acc[r.categoria] ||= []).push(r);
      return acc;
    }, {});
    for (const cat of Object.keys(byCat)) {
      m += `\n*${cat}*\n`;
      for (const r of byCat[cat]) {
        if (r.pezzi > 1) {
          m += `• ${r.nome} (${r.formato}) — ${r.qta} x ${r.pezzi}pz x €${r.prezzo.toFixed(2)} = €${r.totale.toFixed(2)} (${r.pezziTot} pz)\n`;
        } else {
          m += `• ${r.nome} (${r.formato}) — ${r.qta} x €${r.prezzo.toFixed(2)} = €${r.totale.toFixed(2)}\n`;
        }
      }
    }
    m += `\n--- TOTALE ---\n`;
    m += `Confezioni: ${totaleConf}\n`;
    m += `Pezzi totali: ${totalePezzi}\n`;
    m += `*TOTALE: €${totaleEuro.toFixed(2)}*\n`;
    return m;
  }, [selectedRows, totaleConf, totalePezzi, totaleEuro]);

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(messaggio);
      toast.success("Copiato", { description: "Messaggio copiato negli appunti" });
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const sendWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(messaggio)}`;
    window.open(url, "_blank");
  };

  const reset = () => {
    setQuantities({});
    toast.success("Ordine svuotato");
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  if (loading || !authorized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="border-b sticky top-0 bg-background z-10">
        <div className="container mx-auto px-3 py-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-primary">Ordine Menci</h1>
          <Button onClick={handleLogout} variant="outline" size="sm">
            <LogOut className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Esci</span>
          </Button>
        </div>
        <div className="container mx-auto px-3 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca prodotto, formato o categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 py-4 space-y-4">

        <Accordion type="multiple" defaultValue={search ? filtered.map((c) => c.categoria) : []}>
          {filtered.map((cat) => {
            const catCount = cat.items.reduce((s, i) => s + (quantities[keyOf(cat.categoria, i.nome, i.formato)] || 0), 0);
            return (
              <AccordionItem key={cat.categoria} value={cat.categoria}>
                <AccordionTrigger className="text-left">
                  <div className="flex items-center gap-2 flex-1 pr-2">
                    <span className="font-semibold">{cat.categoria}</span>
                    <span className="text-xs text-muted-foreground">({cat.items.length})</span>
                    {catCount > 0 && <Badge className="ml-auto">{catCount}</Badge>}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2">
                    {cat.items.map((i, idx) => {
                      const k = keyOf(cat.categoria, i.nome, i.formato);
                      const qta = quantities[k] || 0;
                      const pezzi = getPezzi(k);
                      const subtotale = qta * pezzi * i.prezzo;
                      return (
                        <Card key={k + idx} className={`p-3 ${qta > 0 ? "border-primary bg-primary/5" : ""}`}>
                          <div className="space-y-3 sm:flex sm:items-center sm:gap-4 sm:space-y-0">
                            <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-sm leading-snug">{i.nome}</p>
                                <p className="mt-0.5 text-xs text-muted-foreground">{i.formato}</p>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Prezzo</p>
                                <p className="text-sm font-semibold">€{i.prezzo.toFixed(2)}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex sm:items-end sm:justify-end">
                              <label className="min-w-0">
                                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Pezzi/conf</span>
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  min={1}
                                  value={pezzi}
                                  onChange={(e) => setPezzi(k, parseInt(e.target.value) || 1)}
                                  className="mt-1 h-10 w-full min-w-0 text-center font-semibold sm:w-20"
                                  aria-label="Pezzi per confezione"
                                />
                              </label>

                              <div className="shrink-0">
                                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Confezioni</span>
                                <div className="mt-1 flex h-10 items-center overflow-hidden rounded-md border border-input bg-background">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 rounded-none border-r border-input"
                                    onClick={() => setQty(k, qta - 1)}
                                    aria-label="Diminuisci"
                                  >
                                    <Minus className="h-4 w-4" />
                                  </Button>
                                  <Input
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    value={qta}
                                    onChange={(e) => setQty(k, parseInt(e.target.value) || 0)}
                                    className="h-10 w-12 rounded-none border-0 px-1 text-center font-semibold focus-visible:ring-0 focus-visible:ring-offset-0"
                                    aria-label="Numero confezioni"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 rounded-none border-l border-input"
                                    onClick={() => setQty(k, qta + 1)}
                                    aria-label="Aumenta"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {qta > 0 && (
                              <div className="flex items-center justify-between rounded-md bg-muted/60 px-3 py-2 text-xs font-medium sm:min-w-36 sm:flex-col sm:items-end sm:bg-transparent sm:px-0 sm:py-0">
                                <span className="text-muted-foreground">{qta} × {pezzi} pz</span>
                                <span className="text-sm font-bold text-foreground">€{subtotale.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
        {filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-8">Nessun risultato</p>
        )}
      </div>

      {/* Bottom sticky bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur z-20">
        <div className="container mx-auto px-3 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">
              {totaleConf} conf • {totalePezzi} pz • {selectedRows.length} voc{selectedRows.length === 1 ? "e" : "i"}
            </p>
            <p className="text-lg font-bold">€{totaleEuro.toFixed(2)}</p>
          </div>
          {selectedRows.length > 0 && (
            <Button variant="outline" size="icon" onClick={reset} aria-label="Svuota">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="lg"
            disabled={selectedRows.length === 0}
            onClick={() => setPreviewOpen(true)}
            className="gap-2"
          >
            <ShoppingCart className="h-4 w-4" />
            Genera ordine
          </Button>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Riepilogo ordine</DialogTitle>
          </DialogHeader>
          <Textarea
            value={messaggio}
            readOnly
            className="font-mono text-xs flex-1 min-h-[300px]"
          />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={copyMessage} className="gap-2">
              <Copy className="h-4 w-4" /> Copia
            </Button>
            <Button onClick={sendWhatsApp} className="gap-2">
              <Send className="h-4 w-4" /> Invia su WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ordine;
